import React, { useEffect, useState } from 'react'
import { useSelector, useDispatch } from 'react-redux'
import isEqual from 'lodash/isEqual'
import { useCart } from 'lib/core/hooks/useCart'
import { usePrevious } from 'lib/core/hooks/usePrevious'
import { useCoreContext } from 'lib/core/hooks/useCoreContext'
import { ShippingAddress, BillingAddress } from 'lib/core/data/quote/cart/types'
import { placeOrder } from 'lib/core/data/quote/placeOrder'
import { setLockCheckout } from 'lib/core/data/quote/lockCheckout'
import { setPaymentMethod } from 'lib/core/data/quote/paymentMethod'
import { getPaymentMethod } from 'lib/core/data/quote/paymentMethod/selectors'

const CONTAINER_ID = 'klarna-container'

export interface KlarnaWidgetProps {
  clientToken: string
  paymentMethodCategory: string
}

export const KlarnaWidget = (props: KlarnaWidgetProps) => {
  const { onError } = useCoreContext()
  const {
    clientToken,
    paymentMethodCategory
  } = props

  const dispatch = useDispatch()
  const { cartId, cart } = useCart()
  const [isKlarnaReady, setIsKlarnaRead] = useState(false)
  const { code, initialized } = useSelector(getPaymentMethod(cartId))
  const billingAddress = cart?.data?.billingAddress as BillingAddress
  const shippingAddress = cart?.data?.shippingAddress as ShippingAddress
  const email = cart?.data?.email as string
  const prevBillingAddress = usePrevious(billingAddress)
  const prevShippingAddress = usePrevious(shippingAddress)

  useEffect(() => {
    if (clientToken) {
      // @ts-expect-error
      window.Klarna.Payments.init({
        client_token: clientToken
      })
    }
  }, [clientToken])

  useEffect(() => {
    if (!cartId || !code) {
      return
    }

    if (initialized && isKlarnaReady) {
      dispatch(setPaymentMethod({
        cartId,
        code
      }))
      dispatch(setLockCheckout({
        cartId,
        value: true
      }))
      // @ts-expect-error
      window.Klarna.Payments.authorize(
        {
          payment_method_category: paymentMethodCategory
        },
        {
          shipping_address: getKlarnaAddress(email, shippingAddress),
          billing_address: getKlarnaAddress(email, billingAddress ?? shippingAddress)
        },
        (authRes: Record<string, any>) => {
          const {
            approved,
            authorization_token: authorizationToken
          } = authRes

          dispatch(setLockCheckout({
            cartId,
            value: false
          }))

          if (approved && authorizationToken) {
            dispatch(placeOrder(cartId, cart?.data, {
              method: code,
              additional_data: {
                authorization_token: authorizationToken
              }
            }))
          }
        }
      )
    }
  }, [
    cart?.data,
    isKlarnaReady,
    initialized,
    cartId,
    code,
    email,
    shippingAddress,
    billingAddress,
    paymentMethodCategory,
    dispatch
  ])

  useEffect(() => {
    const hasShippingChanged = !isEqual(shippingAddress, prevShippingAddress)
    const hasBillingChanged = !isEqual(prevBillingAddress, billingAddress)
    if ((!hasShippingChanged && !hasBillingChanged) || isKlarnaReady) {
      return
    }

    setIsKlarnaRead(false)
    try {
      // @ts-expect-error
      window.Klarna.Payments.load(
        {
          container: `#${CONTAINER_ID}`,
          payment_method_category: paymentMethodCategory
        },
        {
          shipping_address: getKlarnaAddress(email, shippingAddress),
          billing_address: getKlarnaAddress(email, billingAddress ?? shippingAddress)
        },
        () => {
          setIsKlarnaRead(true)
        }
      )
    } catch (e) {
      onError?.(e)
    }
  }, [
    isKlarnaReady,
    setIsKlarnaRead,
    code,
    email,
    cartId,
    dispatch,
    shippingAddress,
    billingAddress,
    prevShippingAddress,
    prevBillingAddress,
    paymentMethodCategory,
    onError
  ])

  return (
    <div
      id={CONTAINER_ID}
    />
  )
}

const getKlarnaAddress = (email: string, address: BillingAddress | ShippingAddress) => { // TODO: type response
  const {
    firstName,
    lastName,
    street,
    city,
    postalCode,
    regionCode,
    countryCode,
    phoneNumber
  } = address

  return {
    email,
    given_name: firstName,
    family_name: lastName,
    street_address: street,
    postal_code: postalCode,
    city: city,
    region: regionCode,
    phone: phoneNumber,
    country: countryCode
  }
}
